import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { MfaInput } from './MfaInputField';
import { Button } from '@shared/components/ui/button/Button';
import type { MFAVerificationData } from '../../types/auth.types';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/app/store/hooks';

export const MFAVerify: React.FC = () => {
    const [code, setCode] = useState<string[]>(Array(6).fill(''));
    const [isRecoveryMode, setIsRecoveryMode] = useState(false);
    const [recoveryCode, setRecoveryCode] = useState('');
    const [error, setError] = useState<string>('');
    const [codeSent, setCodeSent] = useState(false);
    const hasInitialized = useRef(false);
    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
    const lastSubmittedRef = useRef<string>('');
    const navigate = useNavigate();
    const token = useAppSelector((state) => state.auth.token);

    const { sendMFA, verifyMFA, mfaStatus, mfaMethod, isLoading, clearError, logoutAndRedirect } = useAuth();

    useEffect(() => {
        if (!token) {
            navigate('/login');
        }
    }, [token, navigate]);

    // Auto-send MFA code when component mounts (for email method)
    useEffect(() => {
        if (hasInitialized.current) return;

        const sendInitialCode = async () => {
            if (mfaStatus === 'required' && mfaMethod === 'email' && !codeSent) {
                try {
                    hasInitialized.current = true;
                    await sendMFA();
                    setCodeSent(true);
                } catch {
                    setError('Failed to send verification code. Please try again.');
                    hasInitialized.current = false;
                }
            } else if (mfaMethod === 'totp') {
                setCodeSent(true);
                hasInitialized.current = true;
            }
        };

        sendInitialCode();
    }, [mfaStatus, mfaMethod, sendMFA, codeSent]);

    const handleSubmit = useCallback(async () => {
        try {
            clearError();
            setError('');

            let verificationData: MFAVerificationData;

            if (isRecoveryMode) {
                if (!recoveryCode.trim()) {
                    setError('Please enter a recovery code');
                    return;
                }
                verificationData = {
                    code: recoveryCode.trim(),
                    method: 'recovery_code',
                };
            } else {
                if (!code || code.length < 6) {
                    setError('Please enter a valid code');
                    return;
                }
                verificationData = { code: code.join('') };
            }

            const result = await verifyMFA(verificationData);
            if (result?.recovery_codes?.length) {
                navigate('/mfa/recovery-codes', {
                    state: { codes: result.recovery_codes },
                    replace: true,
                });
            }
        } catch (err) {
            setError(
                !isRecoveryMode && err instanceof Error
                    ? err.message
                    : isRecoveryMode
                      ? 'Invalid recovery code. Please try again.'
                      : 'Invalid code. Please try again.',
            );
        }
    }, [code, recoveryCode, isRecoveryMode, clearError, verifyMFA, navigate]);

    // Auto-submit when all 6 OTP digits are filled
    useEffect(() => {
        if (isRecoveryMode || isLoading) return;
        if (!code.every((digit) => digit !== '')) return;

        const joined = code.join('');
        if (lastSubmittedRef.current === joined) return;

        lastSubmittedRef.current = joined;
        handleSubmit();
    }, [code, handleSubmit, isLoading, isRecoveryMode]);

    const handleChange = (value: string, index: number) => {
        setError('');
        if (!/^\d?$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        lastSubmittedRef.current = '';

        if (value && index < code.length - 1) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    };

    const handleBlur = () => {};

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pasted = e.clipboardData.getData('Text').replace(/\D/g, '').slice(0, 6);
        if (pasted.length === 6) {
            const newCode = pasted.split('');
            lastSubmittedRef.current = '';
            setCode(newCode);
            inputsRef.current[5]?.focus();
        }
    };

    const handleResendCode = async () => {
        try {
            setError('');
            await sendMFA();
            setCodeSent(true);
        } catch {
            setError('Failed to resend code. Please try again.');
        }
    };

    const handleToggleRecoveryMode = () => {
        setIsRecoveryMode((prev) => !prev);
        setError('');
        setCode(Array(6).fill(''));
        setRecoveryCode('');
        lastSubmittedRef.current = '';
    };

    return (
        <div className="flex flex-col flex-1">
            <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
                <div>
                    <div className="mb-5 sm:mb-8">
                        <h1 className="mb-2 font-semibold text-mp-foreground text-title-sm sm:text-title-md">
                            {isRecoveryMode ? 'Use a recovery code' : 'Verify OTP'}
                        </h1>
                        {!isRecoveryMode && mfaMethod === 'totp' && (
                            <p className="text-sm text-mp-muted">
                                Please enter the 6-digit code from your Authenticator App on your
                                phone.
                            </p>
                        )}
                        {!isRecoveryMode && mfaMethod === 'email' && (
                            <p className="text-sm text-mp-muted">
                                Please enter the 6-digit code sent to your email address.
                            </p>
                        )}
                        {isRecoveryMode && (
                            <p className="text-sm text-mp-muted">
                                Enter one of your single-use recovery codes (e.g.{' '}
                                <span className="font-mono">ABCD-1234</span>).
                            </p>
                        )}
                    </div>
                </div>

                {isRecoveryMode ? (
                    <div className="mb-4">
                        <input
                            type="text"
                            value={recoveryCode}
                            onChange={(e) => {
                                setError('');
                                setRecoveryCode(e.target.value);
                            }}
                            placeholder="XXXX-XXXX"
                            pattern="[A-Za-z0-9-]{8,9}"
                            autoComplete="off"
                            className="w-full px-4 py-3 font-mono text-center text-mp-foreground bg-mp-surface border border-mp-input rounded-lg focus:outline-none focus:ring-2 focus:ring-mp-primary text-sm tracking-widest uppercase"
                        />
                    </div>
                ) : (
                    <div className="flex flex-row justify-center space-x-4 space-y-6">
                        {code.map((digit, idx) => (
                            <MfaInput
                                key={idx}
                                index={idx}
                                value={digit}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                onBlur={handleBlur}
                                onPaste={handlePaste}
                                inputRef={(el, index) => {
                                    inputsRef.current[index] = el;
                                }}
                            />
                        ))}
                    </div>
                )}

                <div>
                    <Button
                        onClick={handleSubmit}
                        disabled={
                            isLoading ||
                            (isRecoveryMode ? !recoveryCode.trim() : code.some((d) => d === ''))
                        }
                        className="w-full"
                        size="sm"
                    >
                        {isLoading ? 'Verifying...' : 'Verify'}
                    </Button>
                </div>

                {error && <p className="mt-6 text-sm text-mp-error text-center">{error}</p>}

                {!isRecoveryMode && mfaMethod === 'email' && (
                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-mp-border"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-mp-surface text-mp-muted">
                                    Didn't recieve a code?
                                </span>
                            </div>
                        </div>
                        <div className="mt-4 text-center text-sm text-mp-muted">
                            <Link
                                to={'#'}
                                onClick={handleResendCode}
                                className="ml-1 font-medium text-mp-link"
                            >
                                Resend here
                            </Link>
                        </div>
                    </div>
                )}

                <div className="mt-6 text-center text-sm text-mp-muted">
                    <button
                        type="button"
                        onClick={handleToggleRecoveryMode}
                        className="font-medium text-mp-link"
                    >
                        {isRecoveryMode
                            ? 'Use authenticator code instead'
                            : 'Use a recovery code instead'}
                    </button>
                </div>

                <div className="mt-4 text-center text-sm text-mp-muted">
                    <button
                        type="button"
                        onClick={logoutAndRedirect}
                        className="font-medium text-mp-link"
                    >
                        Back to login
                    </button>
                </div>
            </div>
        </div>
    );
};
